import { useCallback } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { KgPermissions } from 'studio/constants';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import { action } from './actionUtil';

export const useDeleteAction = (kgPermissions, onDelete) => {
  const { showConfirmationModal } = useConfirmationModal();
  const { successMessage, errorMessage } = useMessage();
  const { withLoader } = useLoader();
  return useCallback(
    (concept, deleteConcept, onConceptDeleted) => {
      const type = concept.type.toLowerCase();
      const identifier = concept.vklIdentifier;
      // deletes the concept after showing a confirmation dialog
      const confirmAndDeleteConcept = onDeleted => {
        showConfirmationModal(() => ({
          title: `Delete ${type}`,
          content: (
            <>
              Do you really want to delete the {type} <code>{identifier}</code>?
            </>
          ),
          confirmButtonText: 'Delete',
          onConfirm: async () => {
            const response = await deleteConcept();
            if (response.success) {
              // the IDs of the files affected by the deletion
              const affectedFileIds = response.data.data;
              successMessage(`The ${type} has successfully been deleted`);
              // execute the callback specified to the action
              onConceptDeleted();
              // execute the callback specified by the caller
              onDeleted(affectedFileIds);
            } else {
              errorMessage(`There was an error deleting the ${type}`);
            }
          },
          confirmDataTest: 'deletionConfirmationDialogConfirm'
        }));
      };
      // handles clicks on the action
      const onClick = async () => {
        // preview the deletion of the concept
        const response = await withLoader(deleteConcept(true));
        // the IDs of the files affected by the deletion
        const affectedFileIds = response.data.data;
        // call 'onDelete' to check if any of the affected files are currently modified
        onDelete(type, identifier, affectedFileIds, confirmAndDeleteConcept);
      };
      return kgPermissions.includes(KgPermissions.Write) && action('delete', DeleteIcon, `Delete ${type}`, onClick);
    },
    [onDelete]
  );
};
